import type {
  CrewGroup,
  KitGroup,
  ProductionFile,
  ScheduleItem,
  ShotSection,
} from '../types/shoot'

export const production = {
  studio: 'Portal Productions',
  title: 'Bloomberg — Smart Cities',
  // 9 Sep 2026: the episode/project name alone, distinct from `client` and
  // `title` — see ShootContext.tsx's ProductionInfo.episodeTitle comment.
  episodeTitle: 'Smart Cities',
  client: 'Bloomberg',
  location: 'Capital Club Dubai',
  address: 'Capital Club, DIFC Gate Village 3, Dubai',
  locationMapUrl: 'https://maps.google.com/?q=Capital+Club+DIFC+Gate+Village+3+Dubai',
  parkingNotes: 'Valet at the DIFC Gate Village 3 entrance — mention Portal Productions.',
  date: 'Monday 21 July',
  callTimes: [
    { id: 'call', label: 'Crew Call', time: '08:00' },
    { id: 'record', label: 'Record', time: '10:30' },
    { id: 'wrap', label: 'Wrap', time: '13:00' },
  ],
  producer: { name: 'Tom Smith', role: 'Producer', phone: '+971 50 118 4402' },
}

export const schedule: ScheduleItem[] = [
  {
    id: 's1',
    title: 'Crew Call',
    start: '08:00',
    end: '08:15',
    duration: '15 mins',
    who: 'Full crew',
    where: 'Capital Club — Lobby',
    note: 'Meet at the lobby desk. Passes are held under Portal Productions.',
  },
  {
    id: 's2',
    title: 'Load-In',
    start: '08:15',
    end: '08:30',
    duration: '15 mins',
    who: 'Camera + Sound',
    where: 'Service lift to Level 3',
    note: 'Service lift only. Keep flight cases out of the guest corridor.',
  },
  {
    id: 's3',
    title: 'Camera + Lighting Setup',
    start: '08:30',
    end: '09:45',
    duration: '75 mins',
    who: 'Camera A + Camera B + Lighting',
    where: 'Studio Room',
    note: 'Three-camera build. Practicals stay on — match colour to window light.',
  },
  {
    id: 's4',
    title: 'Guest Arrival',
    start: '09:45',
    end: '10:00',
    duration: '15 mins',
    who: 'Producer + Client Contact',
    where: 'Capital Club — Lobby',
    note: 'Producer greets at the lobby. Do not start rolling on arrival.',
  },
  {
    id: 's5',
    title: 'Technical Check',
    start: '10:00',
    end: '10:30',
    duration: '30 mins',
    who: 'Sound + Camera A',
    where: 'Studio Room',
    note: 'Levels on all three mics, timecode jam, card and battery swap.',
  },
  {
    id: 's6',
    title: 'Recording',
    start: '10:30',
    end: '11:45',
    duration: '45–60 mins',
    who: 'Host + Guest 1 + Guest 2',
    where: 'Studio Room',
    note: 'Two parts with a short reset. Side profile before down-the-lens.',
  },
  {
    id: 's7',
    title: 'Pick-Ups',
    start: '11:45',
    end: '12:15',
    duration: '30 mins',
    who: 'Host + Camera A',
    where: 'Studio Room',
    note: 'Intro, outro and any re-takes flagged during the record.',
  },
  {
    id: 's8',
    title: 'B-Roll',
    start: '12:15',
    end: '12:45',
    duration: '30 mins',
    who: 'Camera C + Osmo',
    where: 'Terrace + Lobby + Exterior',
    note: 'Roving unit. Hotel exterior and Dubai establishing while light holds.',
  },
  {
    id: 's9',
    title: 'Wrap',
    start: '13:00',
    duration: '30 mins',
    who: 'Full crew',
    where: 'Studio Room',
    note: 'Do not dismantle until the client has approved final framing.',
  },
]

export const crewGroups: CrewGroup[] = [
  {
    id: 'portal',
    title: 'Portal Team',
    members: [
      { id: 'c1', name: 'Tom Smith', role: 'Producer', phone: '+971 50 118 4402' },
      { id: 'c2', name: 'Zeena Zalamea', role: 'Executive Producer', phone: '+971 50 771 9038' },
      { id: 'c3', name: 'John Doe', role: 'Camera A', phone: '+971 55 204 6611' },
      { id: 'c4', name: 'Jane Doe', role: 'Sound', phone: '+971 55 630 2287' },
    ],
  },
  {
    id: 'client',
    title: 'Client Contact',
    members: [
      { id: 'c5', name: 'Jacob Greaves', role: 'Bloomberg', phone: '+971 52 449 1750' },
    ],
  },
]

const IMG = {
  host: 'https://cdn.magicpatterns.com/patterns/generated-images/64342aff-8363-470c-87f0-854c708a2d74.jpg',
  guest: 'https://cdn.magicpatterns.com/patterns/generated-images/a4e2e853-3d73-4d15-9a71-312a40bf2ac0.jpg',
  twoShot: 'https://cdn.magicpatterns.com/patterns/generated-images/c5b88c03-2963-4dc9-8473-a08befbe696f.jpg',
  hotel: 'https://cdn.magicpatterns.com/patterns/generated-images/43d2eded-0211-43cc-af68-4b0eb504ffaf.jpg',
  arrival: 'https://cdn.magicpatterns.com/patterns/generated-images/08925903-0d67-4d3a-880d-fdfdf5bcbc16.jpg',
  hands: 'https://cdn.magicpatterns.com/patterns/generated-images/ec8c4095-c02f-4dd9-970a-4393afbbb240.jpg',
  room: 'https://cdn.magicpatterns.com/patterns/generated-images/5c3e730b-3145-4757-a205-89b7676f3548.jpg',
  dubai: 'https://cdn.magicpatterns.com/patterns/generated-images/ead3414d-f9bf-441a-9f6a-18e4f648ba50.jpg',
  lowAngle: 'https://cdn.magicpatterns.com/patterns/generated-images/540d09dd-f89a-4732-aa6e-89501a49ba20.jpg',
}

// 11 Sep 2026, at Tom's request: the per-section hint line ("Must be in the
// can before wrap", etc.) that used to render under each Essential/B-Roll/
// Optional heading was removed outright — ShotSection no longer has a
// caption field at all (see types/shoot.ts).
export const shotSections: ShotSection[] = [
  {
    id: 'essential',
    title: 'Essential',
    items: [
      { id: 'sh1', title: 'Host', camera: 'Camera A', image: IMG.host },
      { id: 'sh2', title: 'Guest', camera: 'Camera B', image: IMG.guest },
      { id: 'sh3', title: 'Two Shot', camera: 'Camera C', image: IMG.twoShot },
    ],
  },
  {
    id: 'broll',
    title: 'B-Roll',
    items: [
      { id: 'sh4', title: 'Hotel exterior', camera: 'Camera C', image: IMG.hotel },
      { id: 'sh5', title: 'Guest arrival', camera: 'Camera C', image: IMG.arrival },
      { id: 'sh6', title: 'Hands / details', camera: 'Osmo Pocket', image: IMG.hands },
      { id: 'sh7', title: 'Wide room', camera: 'Camera C', image: IMG.room },
      { id: 'sh8', title: 'Dubai establishing', camera: 'Camera C', image: IMG.dubai },
    ],
  },
  {
    id: 'optional',
    title: 'Optional',
    items: [
      { id: 'sh9', title: 'Low angle', camera: 'Camera A', image: IMG.lowAngle },
      { id: 'sh10', title: 'Roving shot', camera: 'Osmo Pocket', image: IMG.room },
      { id: 'sh11', title: 'Walking opening', camera: 'Camera C', image: IMG.arrival },
    ],
  },
]

export const kitGroups: KitGroup[] = [
  {
    id: 'camera',
    title: 'Camera',
    items: [
      { id: 'k1', name: 'Sony FX3', qty: '×3' },
      { id: 'k2', name: '24–70 GM', qty: '×3' },
      { id: 'k3', name: 'Tripods', qty: '×3' },
      { id: 'k4', name: 'Osmo Pocket 3', qty: '×1' },
    ],
  },
  {
    id: 'audio',
    title: 'Audio',
    items: [
      { id: 'k5', name: 'Shure SM7B', qty: '×3' },
      { id: 'k6', name: 'RØDECaster Pro II', qty: '×1' },
      { id: 'k7', name: 'XLR cables', qty: '×4' },
      { id: 'k8', name: 'Headphones', qty: '×3' },
    ],
  },
  {
    id: 'lighting',
    title: 'Lighting',
    items: [
      { id: 'k9', name: 'Nova P300c', qty: '×2' },
      { id: 'k10', name: 'PavoTube', qty: '×2' },
      { id: 'k11', name: 'Stands', qty: '×4' },
    ],
  },
  {
    id: 'media',
    title: 'Media / Power',
    items: [
      { id: 'k12', name: 'SD cards', qty: '×8' },
      { id: 'k13', name: 'Batteries', qty: '×9' },
      { id: 'k14', name: 'Chargers', qty: '×3' },
      { id: 'k15', name: 'Extension cable', qty: '×2' },
    ],
  },
]

/**
 * Merged 8 Sep 2026: this used to be two separate demo lists (Client Notes +
 * Important Instructions) mirroring the two fields that were merged in the
 * real builder. Kept as one list so the demo view matches production.
 */
export const instructions: string[] = [
  'Bloomberg wants the side profile before the down-the-lens shot.',
  'Keep the Capital Club signage out of frame in all wide shots.',
  'Guest 2 joins from 11:00 only — build the first part around Host + Guest 1.',
  'Do not dismantle until client has approved final framing.',
  'No rolling in the lobby or members’ areas without the producer present.',
  'Cards are offloaded and verified on site before anything is formatted.',
]

export const productionFiles: ProductionFile[] = [
  { id: 'f1', title: 'Call Sheet', meta: 'PDF · v3 · 21 Jul' },
  { id: 'f2', title: 'Script', meta: 'PDF · 6 pages' },
  { id: 'f3', title: 'Guest Bios', meta: 'PDF · 2 guests' },
  { id: 'f4', title: 'Storyboard', meta: 'PDF · 11 frames' },
  { id: 'f5', title: 'Dropbox Folder', meta: 'Shared link' },
]
